/**
 * Animates the routed content when the user switches the
 * active top-level workspace section via the left sidebar.
 *
 * The sidebar itself is fixed — it lives in `DashboardLayout`'s `<aside>`
 * and is NOT re-rendered here. Only the routed `<Outlet />` content
 * animates: it fades in over 150ms while rising from 8px → 0px on an
 * ease-out curve, so navigation feels like content changing *within* the
 * workspace rather than a full page reload.
 *
 * The animation re-keys on the TOP-LEVEL path segment only
 * (`/projects/...` → `projects`, `/iq/...` → `iq`, `/` → `home`). Deeper
 * navigations inside a section (e.g. a project's Folders → Folder detail)
 * intentionally do NOT re-trigger this transition — those are owned by the
 * section-local shared-element transition (part 2 of the same ticket).
 *
 * Re-keying a `motion.div` remounts its subtree, which replays the
 * `initial → animate` entrance. No exit animation is specified by the
 * design, so `AnimatePresence` is unnecessary; a keyed entrance keeps the
 * DOM swap instant while the incoming content animates in.
 *
 * Respects `prefers-reduced-motion`: when set, the content renders with no
 * transform/opacity animation.
 */

import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { navContentTransition } from '@/shared/constants/motion.constants';

/**
 * Derives the top-level section key from a pathname. The leading segment
 * is what the sidebar nav switches between; everything below it is
 * section-internal navigation that should not re-trigger the workspace
 * content swap. Falls back to `home` for the index route (`/`).
 */
function topLevelSegment(pathname: string): string {
  const [segment] = pathname.replace(/^\/+/, '').split('/');
  // eslint-disable-next-line i18next/no-literal-string -- route segment key for the transition
  return segment || 'home';
}

export const NavTransitionOutlet = () => {
  const { pathname } = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const sectionKey = useMemo(() => topLevelSegment(pathname), [pathname]);

  if (shouldReduceMotion) {
    return <Outlet />;
  }

  return (
    <motion.div
      key={sectionKey}
      className="h-full"
      initial={{ opacity: 0, y: navContentTransition.offsetY }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: navContentTransition.duration,
        ease: navContentTransition.ease,
      }}
    >
      <Outlet />
    </motion.div>
  );
};

NavTransitionOutlet.displayName = 'NavTransitionOutlet';
